import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { canAccessAgents, canAccessAppBench, canAccessPhysicalAi, canAccessScheduling, isSchedulingStaff, physicalAiModuleEnabled } from '@/lib/product-flags';
import { appSurface } from '@/lib/app-surface';
import type { User } from '@/lib/auth-context';

/** Orca's first page for a user: agents, then the modules moving onto it. */
function orcaHome(user: User | null) {
  if (canAccessAgents(user)) return '/agents/projects';
  if (user?.role === 'participant' || user?.role === 'qa') return '/red-team';
  if (user?.role === 'admin' && user.products?.red_team) return '/admin/red-team/campaigns';
  if (canAccessAppBench(user)) return '/admin/appbench';
  if (user?.products?.promptbench) return '/promptbench/projects';
  if (user?.products?.red_team) return '/red-team';
  return user?.role === 'super_admin' ? '/super-admin/tenants' : '/profile';
}

export function RootRedirect() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (appSurface === 'orca') return <Navigate to={orcaHome(user)} replace />;

  if (isSchedulingStaff(user)) return <Navigate to="/scheduling" replace />;

  if (user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'project_manager') {
    if (user?.tenantAccessMode === 'expired_read_only') {
      if (!user.products?.data_collection && canAccessPhysicalAi(user)) return <Navigate to="/admin/physical-ai" replace />;
      return <Navigate to="/admin/account/projects" replace />;
    }
    if (user?.role !== 'super_admin' && !user?.products?.data_collection) {
      if (user?.products?.red_team) return <Navigate to="/admin/red-team/campaigns" replace />;
      if (canAccessAppBench(user)) return <Navigate to="/admin/appbench" replace />;
      if (canAccessPhysicalAi(user)) return <Navigate to="/admin/physical-ai" replace />;
      if (canAccessScheduling(user)) return <Navigate to="/scheduling" replace />;
    }
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (user?.role === 'participant' && user.products?.physical_ai && physicalAiModuleEnabled && !user.products?.data_collection) return <Navigate to="/physical-ai/work" replace />;
  if (user?.role === 'qa') {
    return <Navigate to="/qa/dashboard" replace />;
  }
  if (!user?.products?.data_collection && user?.products?.red_team) {
    return <Navigate to="/red-team" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}
